/**
 * Technology Modal State Management Hook
 *
 * Manages open/close state for all modals in the Technology section.
 * Provides clean separation between state and handlers.
 *
 * Modals managed:
 * - Note modal (bulk note addition)
 * - Tag modal (bulk tag addition)
 * - Remove tag modal (bulk tag removal)
 * - Export modal (universal export modal)
 *
 * Risk Level: Low
 * - Simple state management
 * - No side effects
 * - No dependencies
 */

import { useState } from 'react';

import type {
  ModalHandlers,
  ModalState,
} from '@/sections/technology/types/modal.types';

/**
 * Return type for useTechnologyModalState hook
 */
interface UseTechnologyModalStateReturn {
  /** Current state of all modals */
  state: ModalState;
  /** Handlers for opening and closing modals */
  handlers: ModalHandlers;
}

/**
 * Custom hook for managing technology modal state
 *
 * Provides centralized state management for all technology-related modals.
 * Each modal has dedicated open/close handlers for clarity.
 *
 * Example usage:
 * ```tsx
 * const { state, handlers } = useTechnologyModalState();
 *
 * <Button onClick={handlers.openNoteModal}>Add Note</Button>
 * <NoteModal
 *   isOpen={state.noteModal}
 *   onClose={handlers.closeNoteModal}
 * />
 * ```
 *
 * @returns Object containing modal state and handlers
 */
export const useTechnologyModalState = (): UseTechnologyModalStateReturn => {
  const [noteModal, setNoteModal] = useState(false);
  const [tagModal, setTagModal] = useState(false);
  const [removeTagModal, setRemoveTagModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);

  const handlers: ModalHandlers = {
    openNoteModal: () => setNoteModal(true),
    closeNoteModal: () => setNoteModal(false),
    openTagModal: () => setTagModal(true),
    closeTagModal: () => setTagModal(false),
    openRemoveTagModal: () => setRemoveTagModal(true),
    closeRemoveTagModal: () => setRemoveTagModal(false),
    openExportModal: () => setExportModal(true),
    closeExportModal: () => setExportModal(false),
  };

  const state: ModalState = {
    noteModal,
    tagModal,
    removeTagModal,
    exportModal,
  };

  return { state, handlers };
};
